<template>
  <section class="support-archive">
    <app-filter-toolbar>
      <template #primary><app-input v-model="search" design="new" type="search" placeholder="Number, subject or requester" aria-label="Search" @input="scheduleLoad" /></template>
      <app-select v-model="resolutions" design="new" :options="resolutionOptions" multiple label="label" placeholder="Resolution" aria-label="Resolution" @input="load(1)" />
      <app-select v-model="assigneesSelected" design="new" :options="assignees" multiple label="full_name" placeholder="Assignee" aria-label="Assignee" @input="load(1)" />
      <template #selection><app-checkbox v-model="unresolved" design="new" label="Unresolved / Technical debt" /></template>
      <template v-if="search || resolutions.length || assigneesSelected.length || unresolved" #clear><AppClearFiltersButton design="new" icon-only @clear="clearFilters" /></template>
    </app-filter-toolbar>

    <app-card design="new" padding="none" class="support-archive__table-card">
      <AppContentSkeleton v-if="loading" label="Loading archived tickets" :columns="columns" min-width="1200px" />
      <AppErrorState v-else-if="error" :message="error" @retry="load" />
      <app-no-results v-else-if="!tickets.length" design="new" title="No archived tickets" text="" />
      <support-ticket-table v-else :tickets="tickets" :columns="columns" min-width="1200px">
        <template #cell-ticket="{ ticket }">
          <button type="button" class="support-archive__ticket" @click="openTicket(ticket)"><AppTableText :text="ticket.public_number" /></button>
        </template>
        <template #cell-subject="{ ticket }"><AppTableText :text="ticket.subject" /></template>
        <template #cell-description="{ ticket }"><AppTableText :text="ticket.description_preview" /></template>
        <template #cell-requester="{ ticket }"><ticket-table-person :user="ticket.requester" empty-text="Unknown requester" /></template>
        <template #cell-classification="{ ticket }"><AppTableText :text="classification(ticket)" /></template>
        <template #cell-assignee="{ ticket }"><ticket-table-person :user="ticket.assignee" /></template>
        <template #cell-resolution="{ ticket }"><app-status-badge design="new" compact :label="resolutionLabel(ticket.resolution)" :tone="resolutionTone(ticket.resolution)" /></template>
        <template #cell-closed="{ ticket }"><span class="support-archive__closed">{{ formatDateTime(ticket.closed_at) }}</span></template>
      </support-ticket-table>
      <AppPagination v-if="!loading && !error && tickets.length"
        placement="embedded"
        design="new"
        :page="page"
        :count="count"
        :visible-count="tickets.length"
        :page-size="pageSize"
        :loading="loading"
        label="Archived ticket pages"
        @change="load"
      />
    </app-card>
  </section>
</template>

<script>
import { getSupportTickets, getSupportTicketUsers } from '@/http'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import AppTableText from '@/components/AppTableText.vue'
import SupportTicketTable from './components/SupportTicketTable'
import TicketTablePerson from './components/TicketTablePerson'
import { apiError, formatDateTime, normalizePage, RESOLUTION_OPTIONS, resolutionLabel } from './ticketState'

export default {
  name: 'SupportTicketArchive',
  components: { AppContentSkeleton, AppErrorState, AppTableText, SupportTicketTable, TicketTablePerson },
  data() {
    return {
      tickets: [],
      count: 0,
      page: 1,
      pageSize: 40,
      loading: true,
      error: '',
      search: '',
      resolutions: [],
      assignees: [],
      assigneesSelected: [],
      unresolved: this.$route.query.preset === 'unresolved',
      resolutionOptions: RESOLUTION_OPTIONS,
      timer: null,
      columns: [
        { key: 'ticket', label: 'Ticket', width: '10%' },
        { key: 'subject', label: 'Subject', width: '12%' },
        { key: 'description', label: 'Description', width: '14%' },
        { key: 'requester', label: 'Requester', width: '13%' },
        { key: 'classification', label: 'Classification', width: '12%' },
        { key: 'assignee', label: 'Assignee', width: '13%' },
        { key: 'resolution', label: 'Resolution', width: '13%' },
        { key: 'closed', label: 'Closed', width: '13%' }
      ]
    }
  },
  watch: {
    unresolved(value) {
      this.resolutions = value ? this.resolutionOptions.filter(item => ['deferred', 'cannot_resolve', 'not_reproduced'].includes(item.value)) : []
      this.load(1)
    }
  },
  created() {
    if (this.unresolved) {
      this.resolutions = this.resolutionOptions.filter(item => ['deferred', 'cannot_resolve', 'not_reproduced'].includes(item.value))
    }
    getSupportTicketUsers({ kind: 'assignee', page_size: 100 }).then(response => { this.assignees = normalizePage(response.data) })
    this.load()
    this.$root.$on('support-ticket-refresh', this.load)
  },
  beforeDestroy() { window.clearTimeout(this.timer); this.$root.$off('support-ticket-refresh', this.load) },
  methods: {
    formatDateTime,
    resolutionLabel,
    resolutionTone(value) { return ['deferred', 'cannot_resolve', 'not_reproduced'].includes(value) ? 'red' : (value === 'resolved' ? 'green' : 'neutral') },
    clearFilters() {
      window.clearTimeout(this.timer)
      this.search = ''
      this.assigneesSelected = []
      if (this.unresolved) this.unresolved = false
      else { this.resolutions = []; this.load(1) }
    },
    scheduleLoad() { window.clearTimeout(this.timer); this.timer = window.setTimeout(() => this.load(1), 250) },
    load(page = this.page) {
      this.page = Math.max(1, page)
      this.loading = true
      this.error = ''
      return getSupportTickets({
        status: 'closed',
        page: this.page,
        page_size: this.pageSize,
        search: this.search || undefined,
        resolution: this.resolutions.map(item => item.value).join(',') || undefined,
        assignee: this.assigneesSelected.map(item => item.id).join(',') || undefined
      }).then(response => {
        this.tickets = normalizePage(response.data)
        this.count = response.data.count || this.tickets.length
      }).catch(error => { this.error = apiError(error) }).finally(() => { this.loading = false })
    },
    openTicket(ticket) { this.$router.push({ name: this.$route.name, query: { ...this.$route.query, ticket: ticket.id } }).catch(() => {}) },
    classification(ticket) { return [ticket.system?.name, ticket.direction?.name, ticket.module?.name].filter(Boolean).join(' / ') || '—' },
    tooltip(content) { return { content: content || '—', placement: 'top', offset: 8, classes: 'tooltip--new' } }
  }
}
</script>

<style lang="scss" scoped>
.support-archive { display: grid; gap: $ui-space-16; }
.support-archive__filters-grid { display: grid; align-items: end; grid-template-columns: 340px 270px 250px auto; gap: $ui-space-12; justify-content: start; }
.support-archive__filters-grid > * { min-width: 0; margin-bottom: 0; }
.support-archive__preset { white-space: nowrap; }
.support-archive__table-card { overflow: hidden; }
.support-archive__table-card :deep(.support-ticket-table th) { white-space: normal; overflow-wrap: anywhere; }
.support-archive__table-card :deep(.app-status-badge) { max-width: 100%; white-space: normal; overflow-wrap: anywhere; }
.support-archive__closed { white-space: normal; }
.support-archive__ticket { max-width: 100%; padding: 0; border: 0; background: transparent; color: $ui-blue; cursor: pointer; font: 700 $ui-font-size-metadata $ui-font-body; }
.support-archive__ticket:focus-visible { border-radius: 3px; outline: 2px solid $ui-blue; outline-offset: 3px; }
.support-archive__ellipsis { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.support-archive__subject { color: $ui-ink; font-weight: 600; }
.support-archive__error { color: $ui-red; }
@media (max-width: 1100px) { .support-archive__filters-grid { grid-template-columns: 320px 250px; } }
@media (max-width: 620px) { .support-archive__filters-grid { grid-template-columns: minmax(0, 1fr); } }
</style>
